import type { JSX } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./hooks/useAuth.js";
import { BearerProvider } from "./hooks/useBearer.js";
import { useOfflineSync } from "./hooks/useOfflineSync.js";
import { ProtectedRoute } from "./components/ProtectedRoute.js";
import { LoginPage } from "./pages/LoginPage.js";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage.js";
import { ResetPasswordPage } from "./pages/ResetPasswordPage.js";
import { ChangePasswordPage } from "./pages/ChangePasswordPage.js";
import { UsersPage } from "./pages/UsersPage.js";
import { CarerGuard } from "./pages/carer/CarerGuard.js";
import { CarerLayout } from "./pages/carer/CarerLayout.js";
import { CarerLoginPage } from "./pages/carer/CarerLoginPage.js";
import { CarerForgotPasswordPage } from "./pages/carer/CarerForgotPasswordPage.js";
import { CarerHomePage } from "./pages/carer/CarerHomePage.js";
import { CarerVisitDetailPage } from "./pages/carer/CarerVisitDetailPage.js";
import { CarerVisitReportPage } from "./pages/carer/CarerVisitReportPage.js";
import { DashboardPage } from "./pages/DashboardPage.js";
import { PatientsPage } from "./pages/PatientsPage.js";
import { PatientDetailPage } from "./pages/PatientDetailPage.js";
import { SrListPage } from "./pages/SrListPage.js";
import { SrDetailPage } from "./pages/SrDetailPage.js";
import { VisitReportPage } from "./pages/VisitReportPage.js";
import { CarersPage } from "./pages/CarersPage.js";
import { CarerDetailPage } from "./pages/CarerDetailPage.js";
import { MedicationsPage } from "./pages/MedicationsPage.js";
import { AutomationPage } from "./pages/AutomationPage.js";
import { HealthPassportPage } from "./pages/HealthPassportPage.js";
import { AuditLogPage } from "./pages/AuditLogPage.js";
import { AnalyticsPage } from "./pages/AnalyticsPage.js";
import { BillingPage } from "./pages/BillingPage.js";
import { BillingDetailPage } from "./pages/BillingDetailPage.js";
import { PaymentConfigPage } from "./pages/PaymentConfigPage.js";
import { DeviceKitsPage } from "./pages/DeviceKitsPage.js";
import { PerformancePage } from "./pages/PerformancePage.js";
import { NsofarSyncPanelPage } from "./pages/NsofarSyncPanelPage.js";
import { ConflictResolutionPage } from "./pages/ConflictResolutionPage.js";
import { CallLogPage } from "./pages/CallLogPage.js";
import { FilteredSrListPage } from "./pages/FilteredSrListPage.js";
import { ConsultOpsPage } from "./pages/ConsultOpsPage.js";
import { AgentsPage } from "./pages/AgentsPage.js";

function wrap(el: JSX.Element): JSX.Element { return <ProtectedRoute>{el}</ProtectedRoute>; }

export function App(): JSX.Element {
  return (
    <>
      <AppRoutes />
      <Toaster position="top-right" richColors />
    </>
  );
}

// Carer-parity S2 / D1c — coordinator subtree wraps in a sessionStorage-backed
// BearerProvider above AuthProvider so useAuth's setAccessToken delegates to
// the right backend.
function CoordinatorTree(): JSX.Element {
  return (
    <BearerProvider backend="sessionStorage" storageKey="hcm_access_token">
      <AuthProvider>
        <Outlet />
      </AuthProvider>
    </BearerProvider>
  );
}

// Carer-parity S2 / D1c — carer subtree wraps in a localStorage-backed
// BearerProvider. CarerGuard + carer pages consume it via useBearer.
function CarerTree(): JSX.Element {
  return (
    <BearerProvider backend="localStorage" storageKey="carer_token">
      <Outlet />
    </BearerProvider>
  );
}

function AppRoutes(): JSX.Element {
  useOfflineSync();
  return (
    <>
      <Routes>
        {/* Carer subtree — mounts its own BearerProvider over localStorage:carer_token. */}
        <Route element={<CarerTree />}>
          <Route path="/carer/login"                      element={<CarerLoginPage />} />
          <Route path="/carer/forgot-password"            element={<CarerForgotPasswordPage />} />
          <Route element={<CarerGuard />}>
            <Route element={<CarerLayout />}>
              <Route path="/carer"                          element={<CarerHomePage />} />
              <Route path="/carer/visit/:id"                element={<CarerVisitDetailPage />} />
              <Route path="/carer/visit/:id/report"         element={<CarerVisitReportPage />} />
            </Route>
          </Route>
        </Route>
        {/* Coordinator subtree — mounts BearerProvider over sessionStorage + AuthProvider. */}
        <Route element={<CoordinatorTree />}>
        <Route path="/login"                              element={<LoginPage />} />
        <Route path="/forgot-password"                    element={<ForgotPasswordPage />} />
        <Route path="/reset-password"                     element={<ResetPasswordPage />} />
        <Route path="/change-password"                    element={wrap(<ChangePasswordPage />)} />
        <Route path="/users"                              element={wrap(<UsersPage />)} />
        <Route path="/dashboard"                          element={wrap(<DashboardPage />)} />
        <Route path="/patients"                           element={wrap(<PatientsPage />)} />
        <Route path="/patients/:id"                       element={wrap(<PatientDetailPage />)} />
        <Route path="/patients/:id/medications"           element={wrap(<MedicationsPage />)} />
        <Route path="/patients/:id/health-passport"       element={wrap(<HealthPassportPage />)} />
        <Route path="/health-passport"                    element={<Navigate to="/patients" replace />} />
        <Route path="/sr"                                 element={wrap(<SrListPage />)} />
        <Route path="/sr/:id"                             element={wrap(<SrDetailPage />)} />
        {/* nsofar consult ops queue (docs/33) — age-ordered second console. */}
        <Route path="/consult-ops"                        element={wrap(<ConsultOpsPage />)} />
        <Route path="/sr/:id/report"                      element={wrap(<VisitReportPage />)} />
        {/* Module SR pages filtered by service_type. Q7 D13 13a — values are the
            canonical 10 enforced by the Mongoose schema; pre-Q7 Nsofar wire stems
            (e.g. "nutritionist") would resolve to zero results post-cutover. PM-JAY
            module dropped per Q7 S3 closure of TASK-44 — pmjay is not part of the
            canonical vocabulary; the module returns when a proper PMJAY workflow lands. */}
        <Route path="/modules/medicine-delivery"          element={wrap(<FilteredSrListPage serviceType="medicine_delivery" moduleTitle="Medicine Delivery" moduleIcon="💊" />)} />
        <Route path="/modules/diagnostics"                element={wrap(<FilteredSrListPage serviceType="diagnostics_visit" moduleTitle="Diagnostics" moduleIcon="🔬" />)} />
        <Route path="/modules/nutritionist"               element={wrap(<FilteredSrListPage serviceType="nutritionist_call" moduleTitle="Nutritionist" moduleIcon="🥗" />)} />
        <Route path="/modules/property-management"        element={wrap(<FilteredSrListPage serviceType="property_visit" moduleTitle="Property Management" moduleIcon="🏠" />)} />
        <Route path="/carers"                             element={wrap(<CarersPage />)} />
        <Route path="/carers/:id"                         element={wrap(<CarerDetailPage />)} />
        <Route path="/agents"                             element={wrap(<AgentsPage />)} />
        <Route path="/automation"                         element={wrap(<AutomationPage />)} />
        <Route path="/analytics"                          element={wrap(<AnalyticsPage />)} />
        <Route path="/billing"                            element={wrap(<BillingPage />)} />
        <Route path="/billing/:id"                        element={wrap(<BillingDetailPage />)} />
        <Route path="/settings/payments"                  element={wrap(<PaymentConfigPage />)} />
        <Route path="/audit-logs"                         element={wrap(<AuditLogPage />)} />
        <Route path="/device-kits"                        element={wrap(<DeviceKitsPage />)} />
        <Route path="/performance"                        element={wrap(<PerformancePage />)} />
        <Route path="/nsofar-sync"                        element={wrap(<NsofarSyncPanelPage />)} />
        <Route path="/conflicts"                          element={wrap(<ConflictResolutionPage />)} />
        <Route path="/call-log"                           element={wrap(<CallLogPage />)} />
        <Route path="*"                                   element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Routes>
    </>
  );
}
